import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, User, Calendar, Clock, Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatDateLong } from '../utils/dateFormat';

const Schedule = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'day', 'week', 'month'
  const [technicians, setTechnicians] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [serviceReminders, setServiceReminders] = useState([]);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    technician: '',
    orderNumber: '',
    contract: ''
  });
  const [draggedItem, setDraggedItem] = useState(null);
  const scheduleRef = useRef(null);

  // Time slots from 9:00 AM to 10:00 PM
  const timeSlots = Array.from({ length: 26 }, (_, i) => {
    const hour = Math.floor(9 + i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });

  useEffect(() => {
    fetchTechnicians();
    fetchUnassignedOrders();
    fetchSchedule();
  }, [currentDate, view]);



  const fetchTechnicians = async () => {
    try {
      const response = await axios.get('/api/technicians/available');
      setTechnicians(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Mock data for development
      setTechnicians([
        { id: 1, name: 'Usman Farooq', specialization: 'Engine' },
        { id: 2, name: 'Ahmed Ali', specialization: 'Electrical' },
        { id: 3, name: 'Hassan Khan', specialization: 'Body Work' }
      ]);
    }
  };

  const fetchUnassignedOrders = async () => {
    try {
      const response = await axios.get('/api/job-cards', {
        params: { status: 'PENDING', assigned: false }
      });
      setUnassignedOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Mock data for development
      setUnassignedOrders([
        { id: 1, orderNo: 'JC001', customer: { name: 'John Doe' }, vehicle: { brand: { name: 'Toyota' }, model: { name: 'Camry' } }, services: [{ service: { name: 'Oil Change' } }] },
        { id: 2, orderNo: 'JC002', customer: { name: 'Jane Smith' }, vehicle: { brand: { name: 'Honda' }, model: { name: 'Civic' } }, services: [{ service: { name: 'Brake Service' } }] }
      ]);
    }
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      if (view === 'week') {
        const weekDates = getWeekDates();
        startDate = weekDates[0];
        endDate = new Date(weekDates[6]);
        endDate.setHours(23, 59, 59, 999);
      } else if (view === 'month') {
        const monthDates = getMonthDates();
        startDate = monthDates[0];
        endDate = new Date(monthDates[monthDates.length - 1]);
        endDate.setHours(23, 59, 59, 999);
      } else {
        startDate = new Date(currentDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(currentDate);
        endDate.setHours(23, 59, 59, 999);
      }
      
      const response = await axios.get('/api/calendar/events', {
        params: { 
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      });
      
      const events = Array.isArray(response.data) ? response.data : [];
      
      // Transform data into schedule format
      const scheduleData = {};
      events.forEach(event => {
        const techId = event.technicianId || event.extendedProps?.technicianId;
        if (techId) {
          const timeSlot = event.start.split('T')[1].substring(0, 5);
          if (!scheduleData[techId]) scheduleData[techId] = {};
          scheduleData[techId][timeSlot] = event;
        }
      });
      
      setSchedule(scheduleData);
      
      // Get service reminders from API
      const reminderResponse = await axios.get('/api/reminders');
      const apiReminders = reminderResponse.data.reminders || [];
      
      // Add CRM events as reminders
      const crmEvents = events.filter(e => e.extendedProps?.eventType === 'CRM' || e.extendedProps?.type === 'reminder');
      const allReminders = [
        ...apiReminders,
        ...crmEvents.map(e => ({
          id: e.id.startsWith('reminder-') ? e.id : `event-${e.id}`,
          dueDate: e.start,
          serviceType: e.title.replace('🔔 ', ''),
          customer: { name: e.extendedProps?.user || 'CRM Lead' },
          isCrmEvent: true,
          referenceId: e.extendedProps?.referenceId
        }))
      ];
      
      setServiceReminders(allReminders);
    } catch (error) {
      setSchedule({});
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTimeSlot = () => {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    
    if (hour < 9 || hour >= 22) return null;
    
    const timeSlot = minute < 30 ? 
      `${hour.toString().padStart(2, '0')}:00` : 
      `${hour.toString().padStart(2, '0')}:30`;
    
    return timeSlot;
  };

  const isCurrentTimeSlot = (timeSlot) => {
    const currentSlot = getCurrentTimeSlot();
    return currentSlot === timeSlot;
  };

  const handleDragStart = (e, order) => {
    setDraggedItem(order);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, technicianId, timeSlot) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    try {
      const appointmentDateTime = new Date(currentDate);
      const [hour, minute] = timeSlot.split(':');
      appointmentDateTime.setHours(parseInt(hour), parseInt(minute), 0, 0);

      await axios.patch(`/api/jobcards/${draggedItem.id}/schedule`, {
        technicianId,
        appointmentDate: appointmentDateTime.toISOString()
      });

      toast.success('Job card scheduled successfully');
      fetchSchedule();
      fetchUnassignedOrders();
    } catch (error) {
      toast.error('Failed to schedule job card');
    } finally {
      setDraggedItem(null);
    }
  };

  const _navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDates = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      return date;
    });
  };

  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const _lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getRemindersForDate = (date) => {
    return serviceReminders.filter(reminder => {
      const reminderDate = new Date(reminder.dueDate);
      return reminderDate.toDateString() === date.toDateString();
    });
  };

  const getReminderStyle = (reminder) => {
    return reminder.isCrmEvent 
      ? 'bg-blue-100 border-blue-300 text-blue-800'
      : 'bg-yellow-100 border-yellow-300 text-yellow-800';
  };

  const navigateView = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const handleReminderClick = (reminder) => {
    if (reminder.isCrmEvent) {
      const leadId = reminder.referenceId || reminder.id.toString().replace('crm-', '');
      navigate(`/crm/leads/${leadId}`);
    } else {
      setSelectedReminder(reminder);
      setShowReminderModal(true);
    }
  };

  const handleEditReminder = () => {
    setShowReminderModal(false);
    navigate('/service-reminder', { state: { editReminder: selectedReminder } });
  };

  const filteredUnassignedOrders = unassignedOrders.filter(order => {
    if (filters.orderNumber && !order.orderNo.toLowerCase().includes(filters.orderNumber.toLowerCase())) {
      return false;
    }
    if (filters.contract && !order.contractType?.toLowerCase().includes(filters.contract.toLowerCase())) {
      return false;
    }
    return true;
  });

  const DayView = () => (
    <div className="min-w-full">
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex">
          <div className="w-20 p-3 border-r border-gray-200">
            <span className="text-sm font-medium text-gray-500">Time</span>
          </div>
          {technicians.map(tech => (
            <div key={tech.id} className="flex-1 min-w-48 p-3 border-r border-gray-200">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="font-medium text-gray-900">{tech.name}</div>
                  <div className="text-xs text-gray-500">{tech.specialization}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="relative">
        {timeSlots.map(timeSlot => (
          <div key={timeSlot} className="flex border-b border-gray-100">
            <div className="w-20 p-2 border-r border-gray-200 text-sm text-gray-600 bg-gray-50">
              {timeSlot}
            </div>
            {technicians.map(tech => {
              const appointment = schedule[tech.id]?.[timeSlot];
              const isCurrentTime = isCurrentTimeSlot(timeSlot);
              return (
                <div
                  key={`${tech.id}-${timeSlot}`}
                  className={`flex-1 min-w-48 p-2 border-r border-gray-200 min-h-12 relative ${
                    isCurrentTime ? 'bg-green-50' : ''
                  }`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, tech.id, timeSlot)}
                >
                  {appointment ? (
                    <div className="bg-blue-100 border border-blue-300 rounded p-2 text-sm">
                      <div className="font-medium text-blue-900">{appointment.title}</div>
                      <div className="text-blue-700 text-xs">{appointment.customer}</div>
                    </div>
                  ) : (
                    <div className="h-full w-full hover:bg-gray-50 rounded transition-colors"></div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        {getRemindersForDate(currentDate).map(reminder => (
          <div 
            key={reminder.id} 
            onClick={() => handleReminderClick(reminder)}
            className="absolute right-4 top-4 bg-yellow-100 border border-yellow-300 rounded p-2 text-sm max-w-48 cursor-pointer hover:bg-yellow-200 transition-colors"
          >
            <div className="flex items-center space-x-1">
              <Bell className="w-3 h-3 text-yellow-600" />
              <span className="font-medium text-yellow-800">Service Reminder</span>
            </div>
            <div className="text-yellow-700 text-xs">{reminder.customer?.name}</div>
            <div className="text-yellow-600 text-xs">{reminder.serviceType}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const WeekView = () => {
    const weekDates = getWeekDates();
    return (
      <div className="min-w-full">
        <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
          <div className="flex">
            <div className="w-20 p-3 border-r border-gray-200">
              <span className="text-sm font-medium text-gray-500">Time</span>
            </div>
            {weekDates.map(date => (
              <div key={date.toISOString()} className="flex-1 min-w-32 p-3 border-r border-gray-200">
                <div className="text-center">
                  <div className="font-medium text-gray-900">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="text-sm text-gray-500">
                    {date.getDate()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          {timeSlots.map(timeSlot => (
            <div key={timeSlot} className="flex border-b border-gray-100">
              <div className="w-20 p-2 border-r border-gray-200 text-sm text-gray-600 bg-gray-50">
                {timeSlot}
              </div>
              {weekDates.map(date => {
                const reminders = getRemindersForDate(date);
                const dateStr = date.toISOString().split('T')[0];
                const appointments = technicians.flatMap(tech => {
                  const appointment = schedule[tech.id]?.[timeSlot];
                  if (appointment) {
                    const appointmentDate = new Date(appointment.start).toISOString().split('T')[0];
                    if (appointmentDate === dateStr) {
                      return [{ ...appointment, techName: tech.name }];
                    }
                  }
                  return [];
                });
                return (
                  <div key={date.toISOString()} className="flex-1 min-w-32 p-2 border-r border-gray-200 min-h-12 relative">
                    {appointments.map(appointment => (
                      <div key={appointment.id} className="bg-blue-100 border border-blue-300 rounded p-1 text-xs mb-1">
                        <div className="font-medium text-blue-900 truncate">{appointment.title}</div>
                        <div className="text-blue-700 truncate">{appointment.extendedProps?.customer || appointment.extendedProps?.user}</div>
                      </div>
                    ))}
                    {reminders.map(reminder => (
                      <div 
                        key={reminder.id} 
                        onClick={() => handleReminderClick(reminder)}
                        className={`border rounded p-1 text-xs mb-1 cursor-pointer hover:opacity-80 transition-colors ${getReminderStyle(reminder)}`}
                      >
                        <div className="flex items-center space-x-1">
                          <Bell className="w-2 h-2" />
                          <span className="truncate">{reminder.serviceType}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const MonthView = () => {
    const monthDates = getMonthDates();
    const weeks = [];
    for (let i = 0; i < monthDates.length; i += 7) {
      weeks.push(monthDates.slice(i, i + 7));
    }
    
    return (
      <div className="p-4">
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 p-3 text-center text-sm font-medium text-gray-700">
              {day}
            </div>
          ))}
          {monthDates.map(date => {
            const reminders = getRemindersForDate(date);
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = date.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={date.toISOString()}
                className={`bg-white p-2 min-h-24 ${
                  !isCurrentMonth ? 'text-gray-400' : ''
                } ${
                  isToday ? 'bg-blue-50 border-2 border-blue-200' : ''
                }`}
              >
                <div className="text-sm font-medium mb-1">{date.getDate()}</div>
                <div className="space-y-1">
                  {reminders.slice(0, 2).map(reminder => (
                    <div 
                      key={reminder.id} 
                      onClick={() => handleReminderClick(reminder)}
                      className={`border rounded p-1 text-xs cursor-pointer hover:opacity-80 transition-colors ${getReminderStyle(reminder)}`}
                    >
                      <div className="flex items-center space-x-1">
                        <Bell className="w-2 h-2" />
                        <span className="truncate">{reminder.serviceType}</span>
                      </div>
                    </div>
                  ))}
                  {reminders.length > 2 && (
                    <div className="text-xs text-gray-500">+{reminders.length - 2} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Main Schedule Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateView(-1)}
                className="p-2 hover:bg-gray-100 rounded-md"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Today
              </button>
              <button
                onClick={() => navigateView(1)}
                className="p-2 hover:bg-gray-100 rounded-md"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {view === 'month' ? 
                  currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) :
                  view === 'week' ?
                  `Week of ${getWeekDates()[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` :
                  currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                }
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              {['day', 'week', 'month'].map(viewType => (
                <button
                  key={viewType}
                  onClick={() => setView(viewType)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    view === viewType 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {viewType.charAt(0).toUpperCase() + viewType.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Schedule Views */}
        <div className="flex-1 overflow-auto" ref={scheduleRef}>
          {view === 'day' && <DayView />}
          {view === 'week' && <WeekView />}
          {view === 'month' && <MonthView />}
        </div>
      </div>

      {/* Reminder Details Modal */}
      {showReminderModal && selectedReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Service Reminder Details</h3>
              <button
                onClick={() => setShowReminderModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Customer</label>
                <p className="text-gray-900">{selectedReminder.customer?.name || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Contact</label>
                <p className="text-gray-900">{selectedReminder.customer?.contact || 'N/A'}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Vehicle</label>
                <p className="text-gray-900">
                  {selectedReminder.vehicle ? 
                    `${selectedReminder.vehicle.make} ${selectedReminder.vehicle.model} - ${selectedReminder.vehicle.plateNumber || 'No Plate'}` : 
                    'N/A'
                  }
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Service Type</label>
                <p className="text-gray-900">{selectedReminder.serviceType}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Due Date</label>
                <p className="text-gray-900">
                  {formatDateLong(selectedReminder.dueDate)}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                  PENDING
                </span>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowReminderModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleEditReminder}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit Reminder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
